import type { ApiError as ApiErrorBody } from '@brechoflow/contracts';

/**
 * Cliente HTTP da aplicação.
 * - Autenticação por cookies httpOnly (`credentials: 'include'`); nenhum token no JS.
 * - CSRF por double-submit: header `x-csrf-token` lido do cookie `bf_csrf` nas requisições mutantes.
 * - 401 em rota autenticada tenta `/auth/refresh` uma única vez (single-flight) e repete a chamada.
 */

export const API_BASE = '/api';
const CSRF_COOKIE = 'bf_csrf';
const SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);
/** Rotas em que um 401 significa "credencial errada", não "sessão expirada". */
const NO_REFRESH = [/^\/auth\/(login|register|refresh|forgot-password|reset-password)$/, /^\/auth\/invitations\//];

export class ApiError extends Error {
  readonly statusCode: number;
  readonly code: string;
  readonly issues: { path: string; message: string }[];

  constructor(body: Partial<ApiErrorBody> & { message: string }, statusCode = body.statusCode ?? 0) {
    super(body.message);
    this.name = 'ApiError';
    this.statusCode = statusCode;
    this.code = body.code ?? 'unknown';
    this.issues = body.issues ?? [];
  }

  get isUnauthorized() {
    return this.statusCode === 401;
  }
  get isForbidden() {
    return this.statusCode === 403;
  }
}

/** Erros de política do plano/assinatura (403) tratados de forma global: conta suspensa, limite atingido, recurso indisponível. */
export const POLICY_ERROR_CODES = ['organization_suspended', 'plan_limit_reached', 'feature_unavailable'] as const;
let onPolicyError: ((e: ApiError) => void) | null = null;
/** O app registra aqui o aviso global (toast com link para a assinatura). */
export function setPolicyErrorHandler(fn: ((e: ApiError) => void) | null) {
  onPolicyError = fn;
}

export const isApiError = (e: unknown): e is ApiError => e instanceof ApiError;

export function errorMessage(e: unknown, fallback = 'Algo deu errado. Tente novamente.'): string {
  return isApiError(e) || e instanceof Error ? e.message || fallback : fallback;
}

export function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const hit = document.cookie.split('; ').find((c) => c.startsWith(`${name}=`));
  return hit ? decodeURIComponent(hit.slice(name.length + 1)) : null;
}

type Query = Record<string, string | number | boolean | null | undefined | string[]>;

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  query?: Query;
  signal?: AbortSignal;
  headers?: Record<string, string>;
}

let onSessionExpired: (() => void) | null = null;
/** O provider de sessão registra aqui o que fazer quando o refresh falha. */
export function setSessionExpiredHandler(fn: (() => void) | null) {
  onSessionExpired = fn;
}

export function buildUrl(path: string, query?: Query): string {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(query ?? {})) {
    if (v === undefined || v === null || v === '') continue;
    // lista = chave repetida (?id=a&id=b)
    if (Array.isArray(v)) v.forEach((item) => qs.append(k, item));
    else qs.set(k, String(v));
  }
  const s = qs.toString();
  return `${API_BASE}${path}${s ? `?${s}` : ''}`;
}

async function parseError(res: Response): Promise<ApiError> {
  let body: Partial<ApiErrorBody> | null = null;
  try {
    body = (await res.json()) as Partial<ApiErrorBody>;
  } catch {
    /* corpo não-JSON */
  }
  const message =
    typeof body?.message === 'string' && body.message
      ? body.message
      : res.status >= 500
        ? 'O servidor encontrou um problema. Tente novamente em instantes.'
        : 'Não foi possível concluir a ação.';
  const err = new ApiError({ ...body, message }, res.status);
  if (res.status === 403 && (POLICY_ERROR_CODES as readonly string[]).includes(err.code)) onPolicyError?.(err);
  return err;
}

async function send(path: string, opts: RequestOptions): Promise<Response> {
  const method = opts.method ?? 'GET';
  const headers: Record<string, string> = { Accept: 'application/json', ...opts.headers };
  if (opts.body !== undefined) headers['Content-Type'] = 'application/json';
  if (!SAFE_METHODS.has(method)) {
    const csrf = readCookie(CSRF_COOKIE);
    if (csrf) headers['x-csrf-token'] = csrf;
  }
  try {
    return await fetch(buildUrl(path, opts.query), {
      method,
      headers,
      credentials: 'include',
      body: opts.body === undefined ? undefined : JSON.stringify(opts.body),
      signal: opts.signal,
    });
  } catch (e) {
    if ((e as Error).name === 'AbortError') throw e;
    throw new ApiError({ code: 'network', message: 'Não foi possível conectar ao servidor. Verifique sua conexão.' }, 0);
  }
}

let refreshing: Promise<boolean> | null = null;

/** Renova a sessão uma vez, mesmo com várias chamadas falhando ao mesmo tempo. */
export function refreshSession(): Promise<boolean> {
  refreshing ??= send('/auth/refresh', { method: 'POST' })
    .then((r) => r.ok)
    .catch(() => false)
    .finally(() => {
      refreshing = null;
    });
  return refreshing;
}

/** Envia a requisição e, em 401, renova a sessão uma vez e repete. Erros HTTP ficam por conta de quem chama. */
async function sendWithRefresh(path: string, opts: RequestOptions): Promise<Response> {
  let res = await send(path, opts);
  if (res.status === 401 && !NO_REFRESH.some((re) => re.test(path))) {
    if (await refreshSession()) {
      res = await send(path, opts);
    }
    if (res.status === 401) {
      onSessionExpired?.();
    }
  }
  return res;
}

export async function api<T = unknown>(path: string, opts: RequestOptions = {}): Promise<T> {
  const res = await sendWithRefresh(path, opts);
  if (!res.ok) throw await parseError(res);
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

/** Nome do arquivo no Content-Disposition (aceita filename*=UTF-8'' e filename="..."). */
function dispositionFilename(header: string | null): string | null {
  if (!header) return null;
  const star = /filename\*\s*=\s*(?:UTF-8|utf-8)''([^;]+)/.exec(header);
  if (star?.[1]) {
    try {
      return decodeURIComponent(star[1].trim());
    } catch {
      /* cai para o filename simples */
    }
  }
  const plain = /filename\s*=\s*(?:"([^"]*)"|([^;]+))/.exec(header);
  return (plain?.[1] ?? plain?.[2] ?? '').trim() || null;
}

export interface BlobOptions {
  query?: Query;
  accept?: string;
  signal?: AbortSignal;
  /** Nome usado quando o servidor não envia `Content-Disposition`. */
  fallbackName?: string;
}

/** Resposta binária (PDF, planilha...) com sessão por cookie e renovação automática. Erros HTTP viram `ApiError`. */
export async function apiBlob(path: string, opts: BlobOptions & { fallbackName: string }): Promise<{ blob: Blob; filename: string }>;
export async function apiBlob(path: string, opts?: BlobOptions): Promise<{ blob: Blob; filename: string | null }>;
export async function apiBlob(path: string, opts: BlobOptions = {}): Promise<{ blob: Blob; filename: string | null }> {
  const res = await sendWithRefresh(path, { query: opts.query, signal: opts.signal, headers: { Accept: opts.accept ?? 'application/pdf' } });
  if (!res.ok) throw await parseError(res);
  return { blob: await res.blob(), filename: dispositionFilename(res.headers.get('Content-Disposition')) ?? opts.fallbackName ?? null };
}

export const http = {
  get: <T>(path: string, query?: Query, signal?: AbortSignal) => api<T>(path, { query, signal }),
  post: <T>(path: string, body?: unknown) => api<T>(path, { method: 'POST', body }),
  put: <T>(path: string, body?: unknown) => api<T>(path, { method: 'PUT', body }),
  patch: <T>(path: string, body?: unknown) => api<T>(path, { method: 'PATCH', body }),
  delete: <T = void>(path: string) => api<T>(path, { method: 'DELETE' }),
};
